import { useEffect } from "react";
import { usePlaidLink } from "react-plaid-link";

type PlaidLinkInstanceProps = {
  token: string | null;
  receivedRedirectUri: string | null;
  onSuccess: (token: string, metadata: any) => void;
  onEvent: (event: string, metadata: any) => void;
  onReady: (state: { ready: boolean; open: (() => void) | null }) => void;
};

/**
 * Owns a single usePlaidLink instance. usePlaidLink recreates its underlying Plaid instance
 * whenever `token` changes, but does so by force-exiting the old instance and creating the new
 * one in the same effect run, without waiting for teardown - reusing one instance across
 * multiple link sessions (token null -> new token) can leave the new instance unable to open.
 * Keying this component by session forces a full unmount/remount instead, matching the one
 * lifecycle react-plaid-link is known to handle correctly.
 */
export const PlaidLinkInstance = ({
  token,
  receivedRedirectUri,
  onSuccess,
  onEvent,
  onReady,
}: PlaidLinkInstanceProps) => {
  const { ready, open } = usePlaidLink({
    token,
    receivedRedirectUri,
    onSuccess,
    onEvent,
  });

  // `open` is a fresh no-op function every render until `ready`, so depending on it directly
  // would re-fire the effect below forever while !ready. This is stable while !ready (always
  // null) but still tracks a real change in `open` once ready - e.g. an instance swap.
  const readyOpen = ready ? open : null;

  useEffect(() => {
    onReady({ ready, open: readyOpen ? () => readyOpen() : null });
  }, [ready, readyOpen, onReady]);

  return null;
};
